import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, X, AlertCircle, Clock } from "lucide-react";
import type { Task, TaskColumn } from "@/features/tasks/types/task";

interface RemindersBarProps {
  tasks: Task[];
  columns: TaskColumn[];
  onSelectTask: (task: Task) => void;
}

function getDaysDiff(deadline: string): number {
  const now = new Date();
  const due = new Date(deadline);
  const diffMs = due.getTime() - now.getTime();
  return diffMs / (1000 * 60 * 60 * 24);
}

const SESSION_KEY = "tasks_dismissed_reminders";

function getDismissed(): string[] {
  try {
    const val = sessionStorage.getItem(SESSION_KEY);
    return val ? JSON.parse(val) : [];
  } catch {
    return [];
  }
}

function setDismissed(ids: string[]) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(ids));
  } catch {}
}

export const RemindersBar: React.FC<RemindersBarProps> = ({
  tasks,
  columns,
  onSelectTask,
}) => {
  const { t } = useTranslation();
  const [dismissed, setDismissedState] = useState<string[]>(() => getDismissed());

  const lastColumn = columns.length > 0 ? columns[columns.length - 1] : null;

  const reminderTasks = tasks.filter((task) => {
    if (!task.deadline) return false;
    if (dismissed.includes(task.id)) return false;
    if (lastColumn && task.column_id === lastColumn.id) return false;
    const diff = getDaysDiff(task.deadline);
    return diff <= 3;
  });

  const overdueTasks = reminderTasks.filter((t) => getDaysDiff(t.deadline!) < 0);
  const dueSoonTasks = reminderTasks.filter((t) => getDaysDiff(t.deadline!) >= 0);

  const dismiss = (id: string) => {
    const next = [...dismissed, id];
    setDismissedState(next);
    setDismissed(next);
  };

  const dismissAll = () => {
    const ids = reminderTasks.map((t) => t.id);
    const next = [...dismissed, ...ids];
    setDismissedState(next);
    setDismissed(next);
  };

  if (reminderTasks.length === 0) return null;

  const displayTasks = reminderTasks.slice(0, 5);

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-3">
      <Bell className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <p className="text-sm font-medium text-amber-800">
            {t("tasks.reminder_banner_title")}
          </p>
          {overdueTasks.length > 0 && (
            <span className="text-xs bg-red-100 text-red-700 rounded-full px-2 py-0.5 font-medium flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {overdueTasks.length} overdue
            </span>
          )}
          {dueSoonTasks.length > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 font-medium flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {dueSoonTasks.length} due soon
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {displayTasks.map((task) => {
            const diff = getDaysDiff(task.deadline!);
            const isOverdue = diff < 0;
            return (
              <div
                key={task.id}
                className={`group flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1 cursor-pointer transition-colors ${
                  isOverdue
                    ? "bg-red-100 text-red-700 hover:bg-red-200"
                    : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                }`}
              >
                <span
                  className="max-w-[140px] truncate"
                  onClick={() => onSelectTask(task)}
                >
                  {task.title}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    dismiss(task.id);
                  }}
                  className="opacity-60 hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}

          {reminderTasks.length > 5 && (
            <span className="text-xs text-amber-600 rounded-full px-2.5 py-1 bg-amber-100">
              +{reminderTasks.length - 5} more
            </span>
          )}
        </div>
      </div>

      <button
        onClick={dismissAll}
        className="p-1 text-amber-500 hover:text-amber-700 rounded-full shrink-0 transition-colors"
        title="Dismiss all"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
